/**
 * The editor's toolbar — ours, entirely.
 *
 * WHY IT IS NOT @mantine/tiptap's. Read from that package's source, `RichTextEditorToolbar`
 * renders a bare box: no `role="toolbar"`, no `aria-label`, no roving focus and no arrow-key
 * handling. "We own the chrome" therefore includes owning the toolbar's whole keyboard behaviour,
 * not just its buttons (ADR-0008; docs/ui/03-components.md).
 *
 * It matters more than a toolbar usually would, because THIS IS THE EDITOR'S EXIT. Escape moves
 * focus here, and from here Tab continues normally. If focus cannot land here, the editor is a
 * keyboard trap, which WCAG forbids outright.
 */
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import classes from './Editor.module.css';

export interface EditorToolbarHandle {
  /** Move focus onto the toolbar. This is what Escape in the editor calls. */
  focusFirst: () => void;
}

export interface EditorToolbarProps {
  /** Named, because a toolbar with no name is announced as just "toolbar". */
  label: string;
  children: ReactNode;
}

/**
 * A toolbar is ONE Tab stop with arrow keys inside it — the standard toolbar pattern. Anything
 * else costs a reader one Tab press per control before they reach the text.
 */
export const EditorToolbar = forwardRef<EditorToolbarHandle, EditorToolbarProps>(
  function EditorToolbar({ label, children }, ref) {
    const container = useRef<HTMLDivElement>(null);
    const [rovingIndex, setRovingIndex] = useState(0);

    const controls = useCallback((): HTMLButtonElement[] => {
      const root = container.current;
      if (!root) {
        return [];
      }
      return Array.from(root.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    }, []);

    const focusAt = useCallback(
      (index: number) => {
        const buttons = controls();
        if (buttons.length === 0) {
          return;
        }
        const next = ((index % buttons.length) + buttons.length) % buttons.length;
        setRovingIndex(next);
        buttons.forEach((button, position) => {
          button.tabIndex = position === next ? 0 : -1;
        });
        buttons[next]?.focus();
      },
      [controls],
    );

    useImperativeHandle(ref, () => ({ focusFirst: () => focusAt(0) }), [focusAt]);

    const onKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        const buttons = controls();
        const current = buttons.findIndex((button) => button === document.activeElement);
        const from = current === -1 ? rovingIndex : current;

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          focusAt(from + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          focusAt(from - 1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          focusAt(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          focusAt(buttons.length - 1);
        }
        // Escape is NOT handled here. It is the editor's exit key and nothing else; binding it to
        // "close" anywhere in this component is how the exit stops working.
      },
      [controls, focusAt, rovingIndex],
    );

    return (
      <div
        ref={container}
        role="toolbar"
        aria-label={label}
        aria-orientation="horizontal"
        className={classes.toolbar}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    );
  },
);
